import { CommonModule, Location } from '@angular/common'
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation, inject } from '@angular/core'
import { Title } from '@angular/platform-browser'
import { ActivatedRoute, Data, Router, RouterModule } from '@angular/router'
import { NgbModule } from "@ng-bootstrap/ng-bootstrap"
import { Application, ApplicationType, AuthenticationService, ENV, LoggerService, User } from "@bequeathed/ngx-app-core"
import { RouterUtils, SafeHtmlPipe } from "@bequeathed/ngx-utils"
import { CookieService } from 'ngx-cookie-service'
import { Subscription } from 'rxjs/internal/Subscription'
import { ContentService, MessageService, IntercomService } from "@bequeathed/ngx-services"
import { HeaderComponent, FooterComponent } from "@bequeathed/ngx-ui"

const LOG: LoggerService = LoggerService.get('AppComponent')

/**
 * Main app component
 */
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.sass'],
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        NgbModule,
        SafeHtmlPipe,
        // UI
        HeaderComponent,
        FooterComponent,
    ]
})
export class AppComponent implements OnInit, OnDestroy {

    @ViewChild('main') main: ElementRef | undefined

    authServiceSubscription: Subscription | undefined
    currentUser: User | undefined
    homeAppUri: string | undefined
    showCookiesBanner = true
    footerType: string | undefined
    menuCollapsed = true

    protected logoSrc = "https://bequeathedassets.s3.eu-west-2.amazonaws.com/Images/bequeathed-images/logo_willforgood_inverted.png"
    protected branded = false

    private cookieService = inject(CookieService)
    private contentService = inject(ContentService)
    private authenticationService = inject(AuthenticationService)
    private intercomService = inject(IntercomService)
    private location = inject(Location)

    public title = inject(Title)
    public messages = inject(MessageService)
    private route = inject(ActivatedRoute)
    private router = inject(Router)

    constructor() {
        RouterUtils.retrieveData(this.router, this.route, (data: Data) => {
            this.footerType = data['footer']
        })
        LOG.debug('init', 'Loaded footer...')
    }

    get homeApp(): Application {
        return ENV.applications.get(ApplicationType.HOME)!
    }

    logoutAction = () => {
        this.authenticationService.logout()
        window.location.href = `${this.homeAppUri}/login`
    }

    get breadcrumbs(): any[] {
        const breadcrumbs: string[] = this.title.getTitle().split(':')

        return breadcrumbs.map((name: string, index: number) => {
            const breadcrumb: any = {
                name: name,
                url: '',
                active: false
            }

            if (index === (breadcrumbs.length - 1)) {
                breadcrumb.active = true
            }

            return breadcrumb
        })
    }

    ngOnInit(): void {
        this.homeAppUri = ENV.applications.get(ApplicationType.HOME)!.baseUrl
        this.authServiceSubscription = AuthenticationService.authenticated
            .subscribe(
                (isAuthenticated: boolean) => {
                    if (isAuthenticated) {
                        this.currentUser = this.authenticationService.getUser()!
                        this.intercomService.updateIntercomWidget(this.currentUser.userId!, this.currentUser.email!)
                    } else {
                        this.currentUser = undefined
                    }
                }
            )
    }

    ngOnDestroy(): void {
        this.authServiceSubscription?.unsubscribe()
    }

    getCurrentPage(): string {
        return this.location.path()
    }

    areCookiesAccepted(): boolean {
        return !this.showCookiesBanner || this.cookieService.check('evident_accept_cookies')
    }

    acceptCookies(): void {
        this.cookieService.set('evident_accept_cookies', 'accepted', 365, '/', ENV.security.get('mainDomain'))
        this.closeCookieBanner()
    }

    closeCookieBanner(): void {
        this.showCookiesBanner = false
    }

    /**
     * Updates the branding when update event is received
     */
    @HostListener('UpdateBrandingEvent', ['$event'])
    onUpdateBranding(event: any) {
        this.logoSrc = event.detail.large
        this.branded = true
    }

}
